import { Injectable, NgZone } from '@angular/core';
import { LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TatUserDataChanges, UserDataItem } from './auth.service';
import { StorageService } from './storage.service';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { TIMING } from '../constants/app.constants';

export interface NotificationState {
  tools: number; // Renamed from products to tools
  results: number;
  shopping: number;
  invitations: number;
  invoices: number;
  settings: boolean; // true if user data is incomplete
}

export interface DataSnapshot {
  jobs: { [key: string]: any } | null; // Changed from userJobs to jobs (company-related)
  userData: any | null;
  availableTools: { [key: string]: any } | null;
  availableProducts?: { [key: string]: any } | null; // Legacy: kept for backward compatibility
  timestamp: number;
  // Backward compatibility
  userJobs?: { [key: string]: any } | null;
}

@Injectable({
  providedIn: 'root'
})
export class CapacitorNotificationService {
  private readonly STORAGE_KEY = 'app_notifications';
  private readonly SNAPSHOT_KEY = 'app_data_snapshot';
  
  private notificationSubject = new BehaviorSubject<NotificationState>({
    tools: 0, // Renamed from products to tools
    results: 0,
    shopping: 0,
    invitations: 0,
    invoices: 0,
    settings: false
  });

  private lastSnapshot: DataSnapshot | null = null;
  private isInitialized = false;
  private readonly NEW_JOB_IDS_KEY = 'app_new_job_ids';
  private newJobIds: Set<string> = new Set();
  private snapshotLoadPromise: Promise<void> | null = null; // Track snapshot loading promise

  // Debouncing for badge updates
  private badgeUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BADGE_UPDATE_DEBOUNCE = TIMING.BADGE_UPDATE_DEBOUNCE;

  public notifications$: Observable<NotificationState> = this.notificationSubject.asObservable();

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private storageService: StorageService,
    private logger: LoggerService,
    private errorHandler: ErrorHandlerService
  ) {
    // Initialize async - don't await in constructor
    this.loadNotifications().catch(err => this.errorHandler.handleError('Notifications-Load', err));
    this.snapshotLoadPromise = this.loadLastSnapshot().catch(err => {
      this.errorHandler.handleError('Snapshot-Load', err);
    });
    this.loadNewJobIds().catch(err => this.errorHandler.handleError('JobIds-Load', err));
    this.initializeNotifications();
  }
  
  /**
   * Ensure snapshot is loaded before updating notifications
   * This prevents false positives when comparing data
   */
  async ensureSnapshotLoaded(): Promise<void> {
    if (this.snapshotLoadPromise) {
      await this.snapshotLoadPromise;
      this.snapshotLoadPromise = null; // Clear after first wait
    }
    // Also ensure snapshot is loaded if it's still null
    if (!this.lastSnapshot) {
      await this.loadLastSnapshot();
    }
  }

  /**
   * Initialize Capacitor notifications
   */
  private async initializeNotifications(): Promise<void> {
    try {
      // Check if we're running on a native platform
      if (!Capacitor.isNativePlatform()) {
        await this.initializeBrowserNotifications();
        this.isInitialized = true;
        return;
      }

      // Request permissions for local notifications
      const localPerms = await LocalNotifications.requestPermissions();
      if (localPerms.display !== 'granted') {
        this.logger.warn('Local notifications permission not granted');
      }

      // Set up listeners
      this.setupListeners();

      this.isInitialized = true;
    } catch (error) {
      this.errorHandler.handleError('Notifications-Initialize', error);
      // Still mark as initialized for web fallback
      this.isInitialized = true;
    }
  }

  /**
   * Initialize browser notifications
   */
  private async initializeBrowserNotifications(): Promise<void> {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      this.logger.warn('This browser does not support notifications');
      return;
    }

    // Request permission for notifications
    if (Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          this.logger.warn('Browser notification permission denied by user');
        }
      } catch (error) {
        this.errorHandler.handleError('Notifications-Permission-Request', error);
      }
    } else if (Notification.permission !== 'granted') {
      this.logger.warn('Browser notification permission denied or blocked');
    }
  }

  /**
   * Set up notification listeners
   */
  private setupListeners(): void {
    // Only set up listeners on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Local notification listeners
    LocalNotifications.addListener('localNotificationReceived', (notification: any) => {
      // Local notification received
    });

    LocalNotifications.addListener('localNotificationActionPerformed', (notification: any) => {
      this.handleNotificationAction(notification.notification);
    });
  }

  /**
   * Get current notification state
   */
  getNotifications(): NotificationState {
    return this.notificationSubject.value;
  }

  /**
   * Set notification count for a specific type
   */
  async setNotification(type: keyof NotificationState, value: number | boolean): Promise<void> {
    const current = this.getNotifications();
    const updated = { ...current, [type]: value };
    this.notificationSubject.next(updated);
    await this.saveNotifications(updated);
    
    // Update app badge if supported
    await this.updateAppBadge();
  }

  /**
   * Increment notification count for a specific type
   */
  async incrementNotification(type: keyof NotificationState, skipBadgeUpdate: boolean = false): Promise<void> {
    const current = this.getNotifications();
    const currentValue = current[type];
    
    if (typeof currentValue === 'number') {
      const newValue = currentValue + 1;
      
      if (skipBadgeUpdate) {
        // Update without triggering badge update (for batch operations)
        const updated = { ...current, [type]: newValue };
        this.notificationSubject.next(updated);
        await this.saveNotifications(updated);
      } else {
        await this.setNotification(type, newValue);
      }
    }
  }

  /**
   * Batch update multiple notifications at once
   * Reduces localStorage writes and badge updates
   */
  async batchUpdateNotifications(updates: Partial<NotificationState>, skipBadgeUpdate: boolean = false): Promise<void> {
    const current = this.getNotifications();
    const updated = { ...current, ...updates };
    this.notificationSubject.next(updated);
    await this.saveNotifications(updated);
    
    if (!skipBadgeUpdate) {
      await this.updateAppBadge();
    }
  }

  /**
   * Clear notification for a specific type
   */
  async clearNotification(type: keyof NotificationState): Promise<void> {
    const newValue = type === 'settings' ? false : 0;
    await this.setNotification(type, newValue);
  }

  /**
   * Clear all notifications
   */
  async clearAllNotifications(): Promise<void> {
    const cleared: NotificationState = {
      tools: 0, // Renamed from products to tools
      results: 0,
      shopping: 0,
      invitations: 0,
      invoices: 0,
      settings: false
    };
    this.notificationSubject.next(cleared);
    await this.saveNotifications(cleared);
    await this.updateAppBadge();
  }

  /**
   * Check if any notifications are active
   */
  hasActiveNotifications(): boolean {
    const state = this.getNotifications();
    return state.tools > 0 || 
           state.results > 0 || 
           state.shopping > 0 || 
           state.invitations > 0 || 
           state.invoices > 0 || 
           state.settings;
  }

  /**
   * Get total notification count
   */
  getTotalNotificationCount(): number {
    const state = this.getNotifications();
    return state.tools + state.results + state.shopping + state.invitations + state.invoices + (state.settings ? 1 : 0);
  }

  /**
   * Get notification route for a specific type
   */
  getNotificationRoute(type: keyof NotificationState): string {
    const routes: { [key in keyof NotificationState]: string } = {
      tools: '/home/subpages/tools',
      results: '/home/subpages/results',
      shopping: '/home/subpages/shop',
      invitations: '/home/subpages/invitations',
      invoices: '/home/subpages/invoices',
      settings: '/home/subpages/settings'
    };
    return routes[type] || '/home';
  }

  /**
   * Update app badge using local notifications with debouncing
   */
  private async updateAppBadge(): Promise<void> {
    if (!this.isInitialized) return;

    // Clear existing timeout
    if (this.badgeUpdateTimeout) {
      clearTimeout(this.badgeUpdateTimeout);
    }

    // Debounce badge update
    this.badgeUpdateTimeout = setTimeout(async () => {
      // Only update badge on native platforms
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      try {
        const totalCount = this.getTotalNotificationCount();
        
        // Clear all existing notifications first
        await LocalNotifications.cancel({ notifications: [] });

        if (totalCount > 0) {
          // Create a silent notification to update the badge
          const badgeNotification: LocalNotificationSchema = {
            id: 999, // Use a fixed ID for badge updates
            title: '',
            body: '',
            schedule: { at: new Date(Date.now() + 100) } // Schedule immediately
          };

          await LocalNotifications.schedule({ notifications: [badgeNotification] });
        }
      } catch (error) {
        this.errorHandler.handleError('Badge-Update', error);
      }
      
      // Clear timeout reference
      this.badgeUpdateTimeout = null;
    }, this.BADGE_UPDATE_DEBOUNCE);
  }

  /**
   * Show a local notification with navigation data
   * @param autoNavigate - If true, automatically navigates when notification is clicked. Default: false
   */
  async showNotification(title: string, body: string, targetPage?: string, data?: any, autoNavigate: boolean = false): Promise<void> {
    if (!this.isInitialized) return;

    // On web platform, show browser notification
    if (!Capacitor.isNativePlatform()) {
      await this.showBrowserNotification(title, body, targetPage, data, autoNavigate);
      return;
    }

    try {
      const notification: LocalNotificationSchema = {
        id: Date.now(),
        title,
        body,
        schedule: { at: new Date(Date.now() + 1000) },
        // Store navigation data in the notification
        extra: {
          targetPage,
          data,
          autoNavigate
        }
      };

      await LocalNotifications.schedule({ notifications: [notification] });
    } catch (error) {
      this.errorHandler.handleError('Notification-Show', error);
    }
  }

  /**
   * Handle notification action (when user taps notification)
   */
  private handleNotificationAction(notification: any): void {
    if (notification.extra && notification.extra.targetPage && notification.extra.autoNavigate === true) {
      // Navigate first, then clear notification asynchronously to avoid blocking UI
      this.navigateToPage(notification.extra.targetPage);
      
      // Clear the corresponding notification badge asynchronously using NgZone
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(async () => {
          await this.clearNotificationForPage(notification.extra.targetPage);
        });
      });
    }
  }

  /**
   * Navigate to specific page
   */
  private navigateToPage(targetPage: string): void {
    switch (targetPage) {
      case 'tools':
        this.router.navigate(['/home/subpages/tools']);
        break;
      case 'products': // Legacy: kept for backward compatibility
        this.router.navigate(['/home/subpages/tools']);
        break;
      case 'results':
        this.router.navigate(['/home/subpages/results']);
        break;
      case 'shopping':
        this.router.navigate(['/home/subpages/shop']);
        break;
      case 'invitations':
        this.router.navigate(['/home/subpages/invitations']);
        break;
      case 'invoices':
        this.router.navigate(['/home/subpages/invoices']);
        break;
      case 'settings':
        this.router.navigate(['/home/subpages/settings']);
        break;
      default:
        this.logger.warn(`Unknown target page: ${targetPage}`);
    }
  }

  /**
   * Clear notification badge for specific page
   */
  private async clearNotificationForPage(targetPage: string): Promise<void> {
    switch (targetPage) {
      case 'tools':
        await this.clearNotification('tools');
        break;
      case 'products': // Legacy: kept for backward compatibility
        await this.clearNotification('tools');
        break;
      case 'results':
        await this.clearNotification('results');
        break;
      case 'shopping':
        await this.clearNotification('shopping');
        break;
      case 'invitations':
        await this.clearNotification('invitations');
        break;
      case 'invoices':
        await this.clearNotification('invoices');
        break;
      case 'settings':
        await this.clearNotification('settings');
        break;
    }
  }

  /**
   * Show browser notification
   * @param autoNavigate - If true, automatically navigates when notification is clicked. Default: false
   */
  private async showBrowserNotification(title: string, body: string, targetPage?: string, data?: any, autoNavigate: boolean = false): Promise<void> {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      this.logger.warn('Browser does not support notifications');
      return;
    }

    // Check if permission is granted
    if (Notification.permission !== 'granted') {
      this.logger.warn('Browser notification permission not granted');
      return;
    }

    try {
      // Create browser notification
      const notification = new Notification(title, {
        body: body,
        icon: '/assets/icon/favicon.png', // Use app icon
        badge: '/assets/icon/favicon.png',
        tag: targetPage || 'general', // Group notifications by target page
        requireInteraction: true, // Keep notification visible until user interacts
        data: {
          targetPage,
          autoNavigate,
          ...data
        }
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        
        // Focus the window
        window.focus();
        
        // Navigate to target page only if autoNavigate is true
        if (targetPage && autoNavigate) {
          this.navigateToPage(targetPage);
          // Clear notification asynchronously using NgZone
          this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(async () => {
              await this.clearNotificationForPage(targetPage);
            });
          });
        }
        
        // Close the notification
        notification.close();
      };

      // Handle notification close
      notification.onclose = () => {
        // Notification closed
      };

      // Auto-close after 10 seconds if not clicked
      setTimeout(() => {
        notification.close();
      }, 10000);

    } catch (error) {
      this.errorHandler.handleError('Browser-Notification-Show', error);
    }
  }

  /**
   * Request browser notification permission manually
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      if (!('Notification' in window)) {
        this.logger.warn('Browser does not support notifications');
        return false;
      }

      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        this.logger.warn('Notification permission denied. User needs to enable it manually in browser settings.');
        return false;
      }

      try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
      } catch (error) {
        this.errorHandler.handleError('Notification-Permission-Request', error);
        return false;
      }
    }
    
    return true; // Native platforms don't need manual permission request
  }

  /**
   * Check if notifications are available
   */
  isNotificationAvailable(): boolean {
    if (!Capacitor.isNativePlatform()) {
      return 'Notification' in window && Notification.permission === 'granted';
    }
    return this.isInitialized;
  }

  /**
   * Send a notification with navigation to a specific page
   * Navigation will happen automatically when the notification is clicked
   */
  async sendNotificationWithNavigation(
    title: string, 
    body: string, 
    targetPage: 'tools' | 'products' | 'results' | 'shopping' | 'invitations' | 'invoices' | 'settings', // 'products' kept for backward compatibility
    data?: any
  ): Promise<void> {
    await this.showNotification(title, body, targetPage, data, true);
  }

  /**
   * Send a notification without navigation (no target page)
   */
  async sendNotificationWithoutNavigation(
    title: string, 
    body: string, 
    data?: any
  ): Promise<void> {
    await this.showNotification(title, body, undefined, data);
  }

  /**
   * Update notifications based on tat user data changes
   * Optimized to use batch increments
   * Only shows notifications for actual new changes, not for data that was already there
   */
  async updateNotificationsFromTatUserDataChanges(changes: TatUserDataChanges): Promise<void> {
    // Don't show notifications if there are no changes
    if (!changes.hasChanges) {
      return;
    }
    
    // Don't show notifications if changes are empty (baseline didn't exist - first load scenario)
    // This prevents showing notifications for data that was already there when app was first opened
    if (changes.newEntries.length === 0 && changes.levelChanges.length === 0) {
      return;
    }

    // Collect all affected job IDs from new entries and level changes
    const affectedJobIds = new Set<string>();
    let resultsIncrementCount = 0;

    // Handle new entries - count actual number of new entries
    if (changes.newEntries.length > 0) {
      resultsIncrementCount += changes.newEntries.length;
      
      // Extract job IDs from new entries
      changes.newEntries.forEach(entry => {
        if (entry.jid) {
          const normalizedJid = String(entry.jid).trim();
          affectedJobIds.add(normalizedJid);
        }
      });
    }

    // Handle level changes - only notify for changes TO Level 2 (green)
    // Filter to only include changes where new level is 2
    const level2Changes = changes.levelChanges.filter(change => {
      const newLevel = Number(change.newLevel);
      if (!Number.isNaN(newLevel)) {
        return newLevel === 2;
      }
      const levelStr = String(change.newLevel).toLowerCase();
      return levelStr === 'green' || levelStr === '2';
    });

    if (level2Changes.length > 0) {
      // Count actual number of level changes
      resultsIncrementCount += level2Changes.length;

      // Extract job IDs from level changes
      level2Changes.forEach(change => {
        if (change.item && change.item.jid) {
          const normalizedJid = String(change.item.jid).trim();
          affectedJobIds.add(normalizedJid);
        }
      });
    }

    // Batch increment if we have increments to make
    if (resultsIncrementCount > 0) {
      const current = this.getNotifications();
      const currentResults = typeof current.results === 'number' ? current.results : 0;
      const newResults = currentResults + resultsIncrementCount;
      
      // Use batch update to avoid multiple badge updates
      await this.batchUpdateNotifications({ results: newResults }, true);
      
      // Show native notifications
      if (changes.newEntries.length > 0) {
        await this.showNotification(
          'New test results available!',
          `${changes.newEntries.length} new test result(s) are available.`,
          'results',
          { 
            count: changes.newEntries.length,
            type: 'new_entries',
            entries: changes.newEntries
          },
          true
        );
      }
      
      if (level2Changes.length > 0) {
        const levelChangeMessages = Object.entries(
          level2Changes.reduce((acc, change) => {
            const key = `${change.oldLevel}->${change.newLevel}`;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {} as { [key: string]: number })
        ).map(([change, count]) => {
          const [oldLevel, newLevel] = change.split('->');
          const oldLevelText = this.getLevelText(oldLevel);
          const newLevelText = this.getLevelText(newLevel);
          return `${count}x ${oldLevelText} → ${newLevelText}`;
        }).join(', ');

        await this.showNotification(
          'Test results updated!',
          `Level changes: ${levelChangeMessages}`,
          'results',
          { 
            count: level2Changes.length,
            type: 'level_changes',
            changes: level2Changes
          },
          true
        );
      }
      
      // Trigger badge update once at the end
      await this.updateAppBadge();
    }

    // Mark all affected jobs as new
    affectedJobIds.forEach(jobId => {
      this.markJobAsNew(jobId).catch(err => this.errorHandler.handleError('Job-Mark-AsNew', err));
    });
  }

  /**
   * Get human-readable level text
   */
  private getLevelText(level: string): string {
    switch (level) {
      case '0': return '🔴 Red';
      case '1': return '🟡 Yellow';
      case '2': return '🟢 Green';
      case 'red': return '🔴 Red';
      case 'yellow': return '🟡 Yellow';
      case 'green': return '🟢 Green';
      default: return level;
    }
  }

  /**
   * Update notifications based on data changes
   */
  async updateNotificationsFromDataChanges(
    jobs: { [key: string]: any } | null,
    userData: any | null,
    availableTools: { [key: string]: any } | null
  ): Promise<void> {
    const currentSnapshot: DataSnapshot = {
      jobs,
      userJobs: jobs, // Backward compatibility
      userData,
      availableTools,
      availableProducts: availableTools, // Legacy: kept for backward compatibility
      timestamp: Date.now()
    };

    // If this is the first snapshot, just store it
    if (!this.lastSnapshot) {
      this.lastSnapshot = currentSnapshot;
      this.saveSnapshot(currentSnapshot);
      return;
    }

    // Detect changes and update notifications
    const changes = this.detectChanges(this.lastSnapshot, currentSnapshot);
    
    // Log detected changes for debugging
    if (changes.hasChanges) {
      this.logger.debug('Data changes detected:', {
        newJobs: changes.newJobs,
        newAvailableTools: changes.newAvailableTools,
        newAvailableToolNames: changes.newAvailableToolNames,
        userDataChanged: changes.userDataChanged
      });
    }
    
    if (changes.hasChanges) {
      // Collect all notification updates for batch processing
      const batchUpdates: Partial<NotificationState> = {};
      const showNotifications: Array<{ title: string; body: string; targetPage: string; data: any }> = [];
      
      // Update notifications based on detected changes
      // TODO: Temporarily disabled - Tool notifications will be fixed later
      // if (changes.newTools > 0) {
      //   const current = this.getNotifications();
      //   const currentTools = typeof current.tools === 'number' ? current.tools : 0;
      //   batchUpdates.tools = currentTools + changes.newTools;
      //   showNotifications.push({
      //     title: 'New tools available!',
      //     body: `You have received ${changes.newTools} new tool(s).`,
      //     targetPage: 'tools',
      //     data: { count: changes.newTools }
      //   });
      // }
      
      // TODO: Temporarily disabled - Tool notifications will be fixed later
      // if (changes.newAvailableTools > 0) {
      //   const current = this.getNotifications();
      //   const currentTools = typeof current.tools === 'number' ? current.tools : 0;
      //   // Increment by the actual count of new available tools
      //   batchUpdates.tools = (batchUpdates.tools ?? currentTools) + changes.newAvailableTools;
      //   
      //   // Create detailed notification message with tool names
      //   const toolNames = changes.newAvailableToolNames || [];
      //   let notificationBody: string;
      //   
      //   if (toolNames.length === 0) {
      //     // Fallback if no names available
      //     notificationBody = `${changes.newAvailableTools} new tool(s) are now available.`;
      //   } else if (toolNames.length === 1) {
      //     notificationBody = `New tool available: ${toolNames[0]}`;
      //   } else if (toolNames.length <= 3) {
      //     // Show all names if 3 or fewer
      //     notificationBody = `New tools available: ${toolNames.join(', ')}`;
      //   } else {
      //     // Show first 3 names and count remaining
      //     const remaining = toolNames.length - 3;
      //     notificationBody = `New tools available: ${toolNames.slice(0, 3).join(', ')}${remaining > 0 ? ` and ${remaining} more` : ''}`;
      //   }
      //   
      //   showNotifications.push({
      //     title: 'New tools available!',
      //     body: notificationBody,
      //     targetPage: 'tools',
      //     data: { 
      //       count: changes.newAvailableTools,
      //       toolNames: toolNames
      //     }
      //   });
      // }
      
      // Apply batch updates if any
      if (Object.keys(batchUpdates).length > 0) {
        await this.batchUpdateNotifications(batchUpdates, true);
        
        // Show all native notifications
        for (const notif of showNotifications) {
          await this.showNotification(notif.title, notif.body, notif.targetPage as any, notif.data, true);
        }
        
        // Trigger badge update once at the end
        await this.updateAppBadge();
      }
      
      if (changes.userDataChanged) {
        await this.updateSettingsNotification(userData);
        
        // Send notification if user data is incomplete
        const isDataIncomplete = this.isUserDataIncomplete(userData);
        
        if (isDataIncomplete) {
          await this.setNotification('settings', true);
          await this.showNotification(
            'Settings incomplete!',
            'Please complete your user data in the settings.',
            'settings',
            { reason: 'incomplete_user_data' },
            true
          );
        }
      }
    }

    // Update snapshot
    this.lastSnapshot = currentSnapshot;
    this.saveSnapshot(currentSnapshot);
  }

  /**
   * Update settings notification based on user data completeness
   */
  private async updateSettingsNotification(userData: any | null): Promise<void> {
    const isDataIncomplete = this.isUserDataIncomplete(userData);
    await this.setNotification('settings', isDataIncomplete);
  }

  /**
   * Update settings notification based on current user data
   * This method can be called from components to check and update settings badge
   */
  async updateSettingsNotificationFromCurrentData(userData: any | null): Promise<void> {
    const isDataIncomplete = this.isUserDataIncomplete(userData);
    await this.setNotification('settings', isDataIncomplete);
  }

  /**
   * Check if user data is incomplete
   */
  private isUserDataIncomplete(userData: any | null): boolean {
    if (!userData) {
      return true; // No data at all means incomplete
    }
    
    // Check if any of the required fields are missing (website is optional)
    const requiredFields = [
      'company',
      'contact_name', 
      'street',
      'street_no',
      'city',
      'zip',
      'country',
      'vat',
      'phone'
    ];
    
    return requiredFields.some(field => {
      const value = userData[field];
      return !value || value.toString().trim() === '';
    });
  }

  /**
   * Get tool display name from tool object
   */
  private getToolDisplayName(tool: any): string {
    if (!tool) return '';
    return tool.tool_name || tool.name || tool.shortname || '';
  }

  /**
   * Detect changes between two data snapshots
   */
  private detectChanges(oldSnapshot: DataSnapshot, newSnapshot: DataSnapshot): {
    hasChanges: boolean;
    newTools: number;
    newJobs: number;
    newAvailableTools: number;
    newAvailableToolNames: string[];
    userDataChanged: boolean;
  } {
    const oldJobs = oldSnapshot.jobs || oldSnapshot.userJobs || {}; // Support both for backward compatibility
    const newJobs = newSnapshot.jobs || newSnapshot.userJobs || {}; // Support both for backward compatibility
    const oldAvailableToolsRaw = oldSnapshot.availableTools || oldSnapshot.availableProducts || null;
    const newAvailableToolsRaw = newSnapshot.availableTools || newSnapshot.availableProducts || null;
    
    // Ensure we have objects, not null/undefined
    const oldAvailableTools = oldAvailableToolsRaw && typeof oldAvailableToolsRaw === 'object' ? oldAvailableToolsRaw : {};
    const newAvailableTools = newAvailableToolsRaw && typeof newAvailableToolsRaw === 'object' ? newAvailableToolsRaw : {};

    // Count new jobs
    const oldJobKeys = Object.keys(oldJobs);
    const newJobKeys = Object.keys(newJobs);
    const newJobCount = newJobKeys.filter(key => !oldJobKeys.includes(key)).length;

    // Count new available tools
    // This includes:
    // 1. Tools that are completely new (new keys)
    // 2. Tools that existed before but became available (isAvailable changed from "0" to "1")
    const oldAvailableToolKeys = Object.keys(oldAvailableTools);
    const newAvailableToolKeys = Object.keys(newAvailableTools);
    
    // Get completely new tools with their names
    const completelyNewToolKeys = newAvailableToolKeys.filter(key => !oldAvailableToolKeys.includes(key));
    const completelyNewToolNames = completelyNewToolKeys
      .map(key => this.getToolDisplayName(newAvailableTools[key]))
      .filter(name => name.length > 0);
    
    // Get tools that became available (existed before but isAvailable changed from "0" to "1")
    const toolsThatBecameAvailableKeys = oldAvailableToolKeys.filter(key => {
      const oldTool = oldAvailableTools[key];
      const newTool = newAvailableTools[key];
      
      if (!newTool) {
        return false; // Tool was removed
      }
      
      // Check if tool became available
      const oldWasAvailable = oldTool?.isAvailable === '1' || oldTool?.isAvailable === 1;
      const newIsAvailable = newTool?.isAvailable === '1' || newTool?.isAvailable === 1;
      
      // Tool became available if it wasn't available before but is now
      return !oldWasAvailable && newIsAvailable;
    });
    const toolsThatBecameAvailableNames = toolsThatBecameAvailableKeys
      .map(key => this.getToolDisplayName(newAvailableTools[key]))
      .filter(name => name.length > 0);
    
    // Combine all new available tool names
    const newAvailableToolNames = [...completelyNewToolNames, ...toolsThatBecameAvailableNames];
    
    // Total count of new available tools
    const newAvailableToolCount = completelyNewToolKeys.length + toolsThatBecameAvailableKeys.length;

    // Check if user data changed
    const userDataChanged = JSON.stringify(oldSnapshot.userData) !== JSON.stringify(newSnapshot.userData);

    const hasChanges = newJobCount > 0 || newAvailableToolCount > 0 || userDataChanged;

    return {
      hasChanges,
      newTools: 0, // userTools is no longer available
      newJobs: newJobCount,
      newAvailableTools: newAvailableToolCount,
      newAvailableToolNames,
      userDataChanged
    };
  }

  /**
   * Load notifications from storage
   */
  private async loadNotifications(): Promise<void> {
    try {
      const notifications = await this.storageService.getJSON<NotificationState>(this.STORAGE_KEY);
      if (notifications) {
        this.notificationSubject.next(notifications);
      }
    } catch (error) {
      this.errorHandler.handleError('Notifications-Load', error);
    }
  }

  /**
   * Save notifications to storage
   */
  private async saveNotifications(notifications: NotificationState): Promise<void> {
    try {
      const success = await this.storageService.set(this.STORAGE_KEY, notifications);
      if (!success) {
        this.logger.warn('Failed to save notifications to storage');
      }
    } catch (error) {
      this.errorHandler.handleStorageError('set', this.STORAGE_KEY, error);
    }
  }

  /**
   * Load last snapshot from individual storage entries (instead of redundant snapshot)
   * This reconstructs the snapshot from the actual data sources
   */
  private async loadLastSnapshot(): Promise<void> {
    try {
      // Reconstruct snapshot from individual storage entries instead of redundant snapshot
      const storedJobs = await this.storageService.getJSON('jobs');
      const storedUserData = await this.storageService.getJSON('userData');
      const storedTools = await this.storageService.getJSON('tools') || await this.storageService.getJSON('products');
      
      if (storedJobs || storedUserData || storedTools) {
        this.lastSnapshot = {
          jobs: storedJobs || null,
          userJobs: storedJobs || null, // Backward compatibility
          userData: storedUserData || null,
          availableTools: storedTools || null,
          availableProducts: storedTools || null, // Legacy: kept for backward compatibility
          timestamp: Date.now()
        };
      }
      
      // Remove old redundant snapshot if it exists
      const oldSnapshot = await this.storageService.get(this.SNAPSHOT_KEY);
      if (oldSnapshot) {
        await this.storageService.remove(this.SNAPSHOT_KEY);
      }
    } catch (error) {
      this.errorHandler.handleError('Snapshot-Load', error);
    }
  }

  /**
   * Save snapshot reference (data is already stored in individual localStorage entries)
   * Note: We no longer save redundant snapshot, data is already in jobs, userData, tools
   */
  private async saveSnapshot(snapshot: DataSnapshot): Promise<void> {
    // Data is already stored in individual storage entries (jobs, userData, tools)
    // No need to save redundant snapshot - just update the in-memory reference
    // Remove old snapshot if it exists
    const oldSnapshot = await this.storageService.get(this.SNAPSHOT_KEY);
    if (oldSnapshot) {
      await this.storageService.remove(this.SNAPSHOT_KEY);
    }
  }

  /**
   * Mark a job as new
   */
  async markJobAsNew(jobId: string): Promise<void> {
    const normalizedJobId = String(jobId).trim();
    this.newJobIds.add(normalizedJobId);
    await this.saveNewJobIds();
  }

  /**
   * Mark a job as seen (remove from new jobs)
   */
  async markJobAsSeen(jobId: string): Promise<void> {
    const normalizedJobId = String(jobId).trim();
    this.newJobIds.delete(normalizedJobId);
    await this.saveNewJobIds();
  }

  /**
   * Check if a job is new
   */
  isJobNew(jobId: string): boolean {
    const normalizedJobId = String(jobId).trim();
    return this.newJobIds.has(normalizedJobId);
  }

  /**
   * Get all new job IDs
   */
  getNewJobIds(): string[] {
    return Array.from(this.newJobIds);
  }

  /**
   * Save new job IDs to storage
   */
  private async saveNewJobIds(): Promise<void> {
    try {
      const success = await this.storageService.set(this.NEW_JOB_IDS_KEY, Array.from(this.newJobIds));
      if (!success) {
        this.logger.warn('Failed to save new job IDs to storage');
      }
    } catch (error) {
      this.errorHandler.handleStorageError('set', this.NEW_JOB_IDS_KEY, error);
    }
  }

  /**
   * Load new job IDs from storage
   */
  private async loadNewJobIds(): Promise<void> {
    try {
      const ids = await this.storageService.getJSON<string[]>(this.NEW_JOB_IDS_KEY);
      if (ids) {
        // Normalize job IDs when loading
        this.newJobIds = new Set(ids.map((id: string) => String(id).trim()));
      }
    } catch (error) {
      this.errorHandler.handleError('JobIds-Load', error);
    }
  }

  /**
   * Reset all notification data (for logout)
   */
  async resetAllData(): Promise<void> {
    // Clear notification badges
    this.clearAllNotifications();
    
    // Clear data snapshot
    this.lastSnapshot = null;
    await this.storageService.remove(this.SNAPSHOT_KEY);
    
    // DON'T clear newJobIds or app_new_job_ids
    // This preserves job "seen" state across logout/login
  }
}
